import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dumbbell, UtensilsCrossed, Heart, ArrowRight, ArrowLeft, Droplets, Zap, Shield, Brain, Flame, Bone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

// Product data with mineral content
const products = [
    {
        id: 'roi',
        name: 'ROI',
        origin: { no: 'Slovenia', en: 'Slovenia' },
        magnesium: 1300,
        calcium: 610,
        sodium: 1600,
        potassium: 17,
        bicarbonate: 7700,
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/pd35iedd_ROI%20bottle.webp',
    },
    {
        id: 'gerolsteiner',
        name: 'Gerolsteiner',
        origin: { no: 'Tyskland', en: 'Germany' },
        magnesium: 108,
        calcium: 348,
        sodium: 118,
        potassium: 11,
        bicarbonate: 1816,
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/ot3qnepg_gerolsteiner-sparkling-glas-0-75-fl-usa.png',
    },
    {
        id: 'vichy-catalan',
        name: 'Vichy Catalan',
        origin: { no: 'Spania', en: 'Spain' },
        magnesium: 9.4,
        calcium: 32,
        sodium: 1097,
        potassium: 49,
        bicarbonate: 2120,
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/wc8yyxxj_Vichy%20Catalan%20bottle.png',
    },
    {
        id: 'marzia',
        name: 'Marzia',
        origin: { no: 'Italia', en: 'Italy' },
        magnesium: 100,
        calcium: 315,
        sodium: 13,
        potassium: 1.2,
        bicarbonate: 571,
        image: 'https://customer-assets.emergentagent.com/job_raw-water-market/artifacts/vr69mse0_marzia-naturale.webp',
    }
];

const MineralGuidePage = () => {
    const { language } = useLanguage();
    const [selectedArea, setSelectedArea] = useState(null);

    const t = (key) => translations[language]?.[key] || translations['no'][key] || key;

    const interestAreas = [
        {
            id: 'training',
            icon: Dumbbell,
            title: t('trainingTitle'),
            description: t('trainingDesc'),
            color: 'from-emerald-600 to-emerald-800'
        },
        {
            id: 'gastronomy',
            icon: UtensilsCrossed,
            title: t('gastronomyTitle'),
            description: t('gastronomyDesc'),
            color: 'from-amber-600 to-amber-800'
        },
        {
            id: 'wellness',
            icon: Heart,
            title: t('wellnessTitle'),
            description: t('wellnessDesc'),
            color: 'from-rose-600 to-rose-800'
        }
    ];

    const guideContent = {
        training: {
            intro: t('trainingIntro'),
            minerals: [
                {
                    name: t('magnesium'),
                    icon: Zap,
                    amount: '300-400 mg/dag',
                    functions: t('trainingMagnesiumFunctions'),
                    products: ['roi', 'gerolsteiner', 'marzia']
                },
                {
                    name: t('calcium'),
                    icon: Bone,
                    amount: '1000-1200 mg/dag',
                    functions: t('trainingCalciumFunctions'),
                    products: ['roi', 'gerolsteiner', 'marzia']
                },
                {
                    name: t('sodium'),
                    icon: Droplets,
                    amount: '1500-2300 mg/dag',
                    functions: t('trainingSodiumFunctions'),
                    products: ['roi', 'vichy-catalan']
                },
                {
                    name: t('bicarbonate'),
                    icon: Shield,
                    amount: '1500+ mg/dag',
                    functions: t('trainingBicarbonateFunctions'),
                    products: ['roi', 'vichy-catalan', 'gerolsteiner']
                }
            ],
            recommendation: t('trainingRecommendation'),
            topProducts: ['roi', 'gerolsteiner', 'vichy-catalan']
        },
        gastronomy: {
            intro: t('gastronomyIntro'),
            minerals: [
                {
                    name: t('bicarbonate'),
                    icon: Shield,
                    amount: t('gastronomyBicarbonateAmount'),
                    functions: t('gastronomyBicarbonateFunctions'),
                    products: ['roi', 'vichy-catalan', 'gerolsteiner']
                },
                {
                    name: t('calcium'),
                    icon: Bone,
                    amount: t('gastronomyCalciumAmount'),
                    functions: t('gastronomyCalciumFunctions'),
                    products: ['roi', 'gerolsteiner', 'marzia']
                },
                {
                    name: t('magnesium'),
                    icon: Zap,
                    amount: t('gastronomyMagnesiumAmount'),
                    functions: t('gastronomyMagnesiumFunctions'),
                    products: ['roi', 'gerolsteiner', 'marzia']
                },
                {
                    name: 'TDS',
                    icon: Droplets,
                    amount: t('gastronomyTDSAmount'),
                    functions: t('gastronomyTDSFunctions'),
                    products: ['roi', 'vichy-catalan', 'gerolsteiner', 'marzia']
                }
            ],
            recommendation: t('gastronomyRecommendation'),
            topProducts: ['gerolsteiner', 'vichy-catalan', 'marzia', 'roi']
        },
        wellness: {
            intro: t('wellnessIntro'),
            minerals: [
                {
                    name: t('magnesium'),
                    icon: Brain,
                    amount: '310-420 mg/dag',
                    functions: t('wellnessMagnesiumFunctions'),
                    products: ['roi', 'gerolsteiner', 'marzia']
                },
                {
                    name: t('calcium'),
                    icon: Bone,
                    amount: '1000-1300 mg/dag',
                    functions: t('wellnessCalciumFunctions'),
                    products: ['roi', 'gerolsteiner', 'marzia']
                },
                {
                    name: t('bicarbonate'),
                    icon: Shield,
                    amount: '1500+ mg/dag',
                    functions: t('wellnessBicarbonateFunctions'),
                    products: ['roi', 'vichy-catalan', 'gerolsteiner']
                },
                {
                    name: t('potassium'),
                    icon: Flame,
                    amount: '3500 mg/dag',
                    functions: t('wellnessPotassiumFunctions'),
                    products: ['vichy-catalan', 'roi']
                }
            ],
            recommendation: t('wellnessRecommendation'),
            topProducts: ['roi', 'marzia', 'gerolsteiner']
        }
    };

    const getProduct = (id) => products.find(p => p.id === id);

    return (
        <div className="page-transition min-h-screen bg-stone-50" data-testid="mineral-guide-page">
            <SEO 
                title={t('pageTitle')}
                description={t('pageDescription')}
                canonical="/mineral-guide"
            />

            {/* Hero Section */}
            <section className="py-20 md:py-28 bg-fjord text-white">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-4">
                            {t('guideSubtitle')}
                        </p>
                        <h1 className="font-display text-4xl md:text-6xl font-light mb-6">
                            {t('guideTitle')}
                        </h1>
                        <p className="text-white/70 text-lg leading-relaxed">
                            {t('guideDescription')}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Selection or Content */}
            <AnimatePresence mode="wait">
                {!selectedArea ? (
                    /* Interest Area Selection */
                    <motion.section
                        key="selection"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="py-20 md:py-28"
                    >
                        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                            <div className="text-center mb-16">
                                <h2 className="font-display text-2xl md:text-3xl font-light mb-4">
                                    {t('questionTitle')}
                                </h2>
                                <p className="text-stone-500">
                                    {t('questionSubtitle')}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                                {interestAreas.map((area, index) => (
                                    <motion.button
                                        key={area.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                        onClick={() => setSelectedArea(area.id)}
                                        className="group text-left p-8 bg-white border border-stone-200 hover:border-fjord/30 hover:shadow-lg transition-all duration-300"
                                        data-testid={`area-${area.id}`}
                                    >
                                        <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${area.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                                            <area.icon className="w-7 h-7 text-white" />
                                        </div>
                                        <h3 className="font-display text-xl mb-3 group-hover:text-fjord transition-colors">
                                            {area.title}
                                        </h3>
                                        <p className="text-stone-500 text-sm leading-relaxed mb-4">
                                            {area.description}
                                        </p>
                                        <span className="inline-flex items-center gap-2 text-sm text-fjord opacity-0 group-hover:opacity-100 transition-opacity">
                                            {t('selectButton')} <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </motion.button>
                                ))}
                            </div>
                        </div>
                    </motion.section>
                ) : (
                    /* Guide Content */
                    <motion.section
                        key="content"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="py-16 md:py-24"
                    >
                        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                            {/* Back Button */}
                            <button
                                onClick={() => setSelectedArea(null)}
                                className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-fjord mb-12 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                {t('backButton')}
                            </button>

                            {/* Area Title */}
                            <div className="mb-12">
                                <p className="text-xs uppercase tracking-[0.2em] text-fjord/60 mb-2">
                                    {interestAreas.find(a => a.id === selectedArea)?.title}
                                </p>
                                <h2 className="font-display text-3xl md:text-4xl font-light mb-6">
                                    {t(`${selectedArea}PageTitle`)}
                                </h2>
                                <p className="text-stone-600 leading-relaxed max-w-3xl">
                                    {guideContent[selectedArea].intro}
                                </p>
                            </div>

                            {/* Minerals Grid */}
                            <div className="mb-16">
                                <h3 className="font-display text-xl mb-8">{t('keyMinerals')}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {guideContent[selectedArea].minerals.map((mineral, index) => (
                                        <motion.div
                                            key={mineral.name}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4, delay: index * 0.1 }}
                                            className="bg-white p-6 border border-stone-200"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="w-12 h-12 rounded-full bg-fjord/10 flex items-center justify-center flex-shrink-0">
                                                    <mineral.icon className="w-6 h-6 text-fjord" />
                                                </div>
                                                <div className="flex-1">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <h4 className="font-display text-lg">{mineral.name}</h4>
                                                        <span className="text-xs text-stone-400">{mineral.amount}</span>
                                                    </div>
                                                    <p className="text-stone-600 text-sm leading-relaxed mb-4">
                                                        {mineral.functions}
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {mineral.products.map(productId => {
                                                            const product = getProduct(productId);
                                                            return (
                                                                <Link
                                                                    key={productId}
                                                                    to={`/products/${productId}`}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 hover:bg-fjord hover:text-white text-xs rounded-full transition-colors"
                                                                >
                                                                    {product?.name}
                                                                </Link>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* Recommendation */}
                            <div className="bg-fjord text-white p-8 md:p-12 mb-16">
                                <h3 className="font-display text-xl mb-4">{t('ourRecommendation')}</h3>
                                <p className="text-white/80 leading-relaxed mb-8 max-w-2xl">
                                    {guideContent[selectedArea].recommendation}
                                </p>
                                
                                {/* Top Products */}
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {guideContent[selectedArea].topProducts.map((productId, index) => {
                                        const product = getProduct(productId);
                                        return (
                                            <Link
                                                key={productId}
                                                to={`/products/${productId}`}
                                                className="group bg-white/10 hover:bg-white/20 p-4 transition-colors"
                                            >
                                                <div className="h-32 md:h-40 mb-3 flex items-center justify-center">
                                                    <img
                                                        src={product?.image}
                                                        alt={product?.name}
                                                        className="h-full w-auto object-contain group-hover:scale-105 transition-transform"
                                                    />
                                                </div>
                                                <p className="text-sm font-medium text-center">{product?.name}</p>
                                                <p className="text-xs text-white/60 text-center">{product?.origin?.[language] || product?.origin}</p>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Mineral Table */}
                            <div className="bg-white border border-stone-200 overflow-hidden">
                                <div className="p-6 border-b border-stone-200">
                                    <h3 className="font-display text-xl">{t('mineralComparison')}</h3>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-stone-50">
                                            <tr>
                                                <th className="text-left p-4 text-xs uppercase tracking-wider text-stone-500">{t('product')}</th>
                                                <th className="text-right p-4 text-xs uppercase tracking-wider text-stone-500">Mg</th>
                                                <th className="text-right p-4 text-xs uppercase tracking-wider text-stone-500">Ca</th>
                                                <th className="text-right p-4 text-xs uppercase tracking-wider text-stone-500">Na</th>
                                                <th className="text-right p-4 text-xs uppercase tracking-wider text-stone-500">K</th>
                                                <th className="text-right p-4 text-xs uppercase tracking-wider text-stone-500">HCO₃</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.map((product, index) => (
                                                <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'}>
                                                    <td className="p-4">
                                                        <Link to={`/products/${product.id}`} className="font-medium hover:text-fjord transition-colors">
                                                            {product.name}
                                                        </Link>
                                                    </td>
                                                    <td className="text-right p-4 text-sm">{product.magnesium}</td>
                                                    <td className="text-right p-4 text-sm">{product.calcium}</td>
                                                    <td className="text-right p-4 text-sm">{product.sodium}</td>
                                                    <td className="text-right p-4 text-sm">{product.potassium}</td>
                                                    <td className="text-right p-4 text-sm">{product.bicarbonate}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-4 border-t border-stone-200 bg-stone-50">
                                    <p className="text-xs text-stone-400">{t('tableNote')}</p>
                                </div>
                            </div>

                            {/* CTA */}
                            <div className="text-center mt-16">
                                <Link
                                    to="/products"
                                    className="inline-flex items-center gap-2 bg-fjord text-white px-8 py-4 hover:bg-fjord/90 transition-colors"
                                >
                                    {t('exploreProducts')} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </motion.section>
                )}
            </AnimatePresence>
        </div>
    );
};

// Translations
const translations = {
    no: {
        pageTitle: 'Mineral Guide',
        pageDescription: 'Lær om mineralenes betydning og finn riktig mineralvann for ditt behov.',
        guideSubtitle: 'Din guide til mineralvann',
        guideTitle: 'Mineral Guide',
        guideDescription: 'Oppdag hvordan naturlige mineraler kan støtte din livsstil. Velg ditt interesseområde for skreddersydd informasjon.',
        questionTitle: 'Hva er ditt interesseområde?',
        questionSubtitle: 'Velg et område for å få tilpasset informasjon om mineraler og anbefalinger',
        selectButton: 'Velg dette',
        backButton: 'Tilbake til valg',
        keyMinerals: 'Viktige mineraler',
        ourRecommendation: 'Vår anbefaling',
        mineralComparison: 'Mineralinnhold per liter (mg/L)',
        product: 'Produkt',
        tableNote: 'Verdier angitt i mg per liter. Kilde: Produsentenes analyser.',
        exploreProducts: 'Utforsk produktene',
        
        // Interest areas
        trainingTitle: 'Trening & Restitusjon',
        trainingDesc: 'Optimaliser prestasjon og restitusjon med riktig mineralbalanse.',
        gastronomyTitle: 'Restaurant & Gastronomi',
        gastronomyDesc: 'Forstå vannets rolle i matopplevelsen og vinharmoni.',
        wellnessTitle: 'Helse & Velvære',
        wellnessDesc: 'Støtt kroppens naturlige funksjoner med essensielle mineraler.',
        
        // Mineral names
        magnesium: 'Magnesium',
        calcium: 'Kalsium',
        sodium: 'Natrium',
        potassium: 'Kalium',
        bicarbonate: 'Bikarbonat',
        
        // Training content
        trainingPageTitle: 'Mineraler for trening og restitusjon',
        trainingIntro: 'Under fysisk aktivitet taper kroppen mineraler gjennom svette – spesielt elektrolytter som natrium, kalium og magnesium. Naturlig mineralvann kan erstatte disse tapene effektivt, uten kunstige tilsetningsstoffer eller sukker. Forskning viser at riktig mineralbalanse er avgjørende for muskelkontraksjon, nervefunksjon og energiomsetning.',
        trainingMagnesiumFunctions: 'Essensielt for muskelavslapning, energiproduksjon og proteinsyntes. Magnesium bidrar til å redusere muskelkramper og fremmer raskere restitusjon etter trening. Over 300 enzymatiske prosesser i kroppen er avhengige av magnesium.',
        trainingCalciumFunctions: 'Nødvendig for muskelkontraksjoner og nervesignaler. Kalsium arbeider sammen med magnesium for å regulere muskeltonus og støtter beintettheten som er viktig for idrettsutøvere.',
        trainingSodiumFunctions: 'Viktig elektrolytt som regulerer væskebalansen og støtter muskelkontraksjoner. Ved intens trening kan natriumtapet være betydelig, og riktig erstatning er kritisk for prestasjon og restitusjon.',
        trainingBicarbonateFunctions: 'Fungerer som en naturlig buffer mot melkesyre som akkumuleres under intens trening. Bikarbonat kan bidra til å forsinke utmattelse og støtte pH-balansen i musklene.',
        trainingRecommendation: 'For aktive personer anbefaler vi en kombinasjon av ROI (ekstremt høyt magnesiuminnhold for restitusjon) og Vichy Catalan (elektrolytter og bikarbonat for utholdenhet). Gerolsteiner er et godt allround-valg for daglig hydrering med moderat mineralinnhold.',
        
        // Gastronomy content
        gastronomyPageTitle: 'Mineralvann i gastronomi',
        gastronomyIntro: 'I finere gastronomi er vannvalg like viktig som vinvalg. Mineralinnholdet påvirker munnfølelse, smaksopplevelse og hvordan vannet komplementerer maten. Et vann med høy mineralitet kan fremheve smaken i rike retter, mens et lettere vann passer bedre til delikate smaker.',
        gastronomyBicarbonateAmount: 'Varierer med rett',
        gastronomyBicarbonateFunctions: 'Gir en myk, silkeaktig munnfølelse og nøytraliserer syre. Høyt bikarbonatinnhold passer godt til fete retter og kan rense ganen mellom retter. Populært valg til vin da det ikke overveldende vinens karakter.',
        gastronomyCalciumAmount: 'Moderat til høy',
        gastronomyCalciumFunctions: 'Bidrar til vannets struktur og mineralitet. Vann med høyere kalsiuminnhold oppleves ofte som mer "substansielt" og passer godt til kraftige kjøttretter og modne oster.',
        gastronomyMagnesiumAmount: 'Moderat',
        gastronomyMagnesiumFunctions: 'Gir en lett bitter undertone som kan fremheve umami og komplekse smaker. Balansert magnesiuminnhold er viktig for at vannet ikke skal dominere matens smak.',
        gastronomyTDSAmount: '500-3000 mg/L',
        gastronomyTDSFunctions: 'Total Dissolved Solids (TDS) indikerer vannets totale mineralinnhold. Lavt TDS (under 500) gir lett, nøytral smak. Middels TDS (500-1500) gir karakter. Høyt TDS (over 1500) gir kraftig mineralsmak som passer til robuste retter.',
        gastronomyRecommendation: 'For restaurant og gastronomi anbefaler vi å tilby et utvalg: Marzia som et elegant, lettere valg til delikate retter og skalldyr. Gerolsteiner som et allsidig valg med god karbonering til hverdagsretter. Vichy Catalan til kraftige smaker og som aperitiff. ROI som en unik opplevelse for gjester som søker noe ekstraordinært.',
        
        // Wellness content
        wellnessPageTitle: 'Mineraler for helse og velvære',
        wellnessIntro: 'Kroppen er avhengig av mineraler for hundrevis av vitale funksjoner – fra nervesignaler og muskelbevegelser til søvnkvalitet og mental klarhet. Naturlig mineralvann gir mineraler i ionisk form, som er den formen kroppen lettest tar opp. I motsetning til kosttilskudd, kommer mineralene i naturlig balanse slik naturen skapte dem.',
        wellnessMagnesiumFunctions: 'Ofte kalt "avslapningsmineralet". Magnesium støtter nervesystemet, fremmer dyp søvn og reduserer stress og angst. Det er også viktig for konsentrasjon og kognitiv funksjon. Mange nordmenn får for lite magnesium gjennom kosten.',
        wellnessCalciumFunctions: 'Essensielt for sterke bein og tenner, men også viktig for hjertefunksjon og nervesignaler. Kalsium i vann absorberes like godt eller bedre enn kalsium fra meieriprodukter, ifølge flere studier.',
        wellnessBicarbonateFunctions: 'Støtter kroppens naturlige syre-base-balanse og fremmer god fordøyelse. Bikarbonat kan lindre halsbrann og sure oppstøt, og støtter leverens avgiftningsprosesser.',
        wellnessPotassiumFunctions: 'Viktig for hjerterytme, blodtrykksregulering og cellebalanse. Kalium arbeider sammen med natrium for å opprettholde væskebalansen og støtte nervefunksjon.',
        wellnessRecommendation: 'For daglig velvære anbefaler vi ROI for de som ønsker maksimal mineraltilførsel, spesielt ved søvnproblemer eller stress. Marzia er et utmerket valg for hverdagsbruk med god balanse mellom kalsium og magnesium, og lavt natriuminnhold. Gerolsteiner passer godt for de som foretrekker kullsyrevann med moderat mineralinnhold.'
    },
    en: {
        pageTitle: 'Mineral Guide',
        pageDescription: 'Learn about the importance of minerals and find the right mineral water for your needs.',
        guideSubtitle: 'Your guide to mineral water',
        guideTitle: 'Mineral Guide',
        guideDescription: 'Discover how natural minerals can support your lifestyle. Select your area of interest for tailored information.',
        questionTitle: 'What is your area of interest?',
        questionSubtitle: 'Choose an area to get customized information about minerals and recommendations',
        selectButton: 'Select this',
        backButton: 'Back to selection',
        keyMinerals: 'Key minerals',
        ourRecommendation: 'Our recommendation',
        mineralComparison: 'Mineral content per liter (mg/L)',
        product: 'Product',
        tableNote: 'Values in mg per liter. Source: Producer analyses.',
        exploreProducts: 'Explore products',
        
        // Interest areas
        trainingTitle: 'Training & Recovery',
        trainingDesc: 'Optimize performance and recovery with the right mineral balance.',
        gastronomyTitle: 'Restaurant & Gastronomy',
        gastronomyDesc: 'Understand the role of water in the dining experience and wine harmony.',
        wellnessTitle: 'Health & Wellness',
        wellnessDesc: 'Support your body\'s natural functions with essential minerals.',
        
        // Mineral names
        magnesium: 'Magnesium',
        calcium: 'Calcium',
        sodium: 'Sodium',
        potassium: 'Potassium',
        bicarbonate: 'Bicarbonate',
        
        // Training content
        trainingPageTitle: 'Minerals for training and recovery',
        trainingIntro: 'During physical activity, the body loses minerals through sweat – especially electrolytes like sodium, potassium, and magnesium. Natural mineral water can effectively replace these losses without artificial additives or sugar. Research shows that proper mineral balance is crucial for muscle contraction, nerve function, and energy metabolism.',
        trainingMagnesiumFunctions: 'Essential for muscle relaxation, energy production, and protein synthesis. Magnesium helps reduce muscle cramps and promotes faster recovery after training. Over 300 enzymatic processes in the body depend on magnesium.',
        trainingCalciumFunctions: 'Necessary for muscle contractions and nerve signals. Calcium works together with magnesium to regulate muscle tone and supports bone density, which is important for athletes.',
        trainingSodiumFunctions: 'Important electrolyte that regulates fluid balance and supports muscle contractions. During intense training, sodium loss can be significant, and proper replacement is critical for performance and recovery.',
        trainingBicarbonateFunctions: 'Acts as a natural buffer against lactic acid that accumulates during intense training. Bicarbonate can help delay fatigue and support pH balance in the muscles.',
        trainingRecommendation: 'For active individuals, we recommend a combination of ROI (extremely high magnesium content for recovery) and Vichy Catalan (electrolytes and bicarbonate for endurance). Gerolsteiner is a good all-round choice for daily hydration with moderate mineral content.',
        
        // Gastronomy content
        gastronomyPageTitle: 'Mineral water in gastronomy',
        gastronomyIntro: 'In fine dining, water selection is as important as wine selection. The mineral content affects mouthfeel, taste experience, and how the water complements the food. Water with high minerality can enhance the flavor of rich dishes, while lighter water is better suited to delicate flavors.',
        gastronomyBicarbonateAmount: 'Varies by dish',
        gastronomyBicarbonateFunctions: 'Provides a soft, silky mouthfeel and neutralizes acidity. High bicarbonate content pairs well with fatty dishes and can cleanse the palate between courses. Popular choice with wine as it doesn\'t overwhelm the wine\'s character.',
        gastronomyCalciumAmount: 'Moderate to high',
        gastronomyCalciumFunctions: 'Contributes to the water\'s structure and minerality. Waters with higher calcium content are often perceived as more "substantial" and pair well with hearty meat dishes and aged cheeses.',
        gastronomyMagnesiumAmount: 'Moderate',
        gastronomyMagnesiumFunctions: 'Provides a slight bitter undertone that can enhance umami and complex flavors. Balanced magnesium content is important so the water doesn\'t dominate the food\'s taste.',
        gastronomyTDSAmount: '500-3000 mg/L',
        gastronomyTDSFunctions: 'Total Dissolved Solids (TDS) indicates the water\'s total mineral content. Low TDS (under 500) gives a light, neutral taste. Medium TDS (500-1500) gives character. High TDS (over 1500) gives a strong mineral taste suitable for robust dishes.',
        gastronomyRecommendation: 'For restaurants and gastronomy, we recommend offering a selection: Marzia as an elegant, lighter choice for delicate dishes and seafood. Gerolsteiner as a versatile choice with good carbonation for everyday dishes. Vichy Catalan for bold flavors and as an aperitif. ROI as a unique experience for guests seeking something extraordinary.',
        
        // Wellness content
        wellnessPageTitle: 'Minerals for health and wellness',
        wellnessIntro: 'The body relies on minerals for hundreds of vital functions – from nerve signals and muscle movements to sleep quality and mental clarity. Natural mineral water provides minerals in ionic form, which is the form the body most easily absorbs. Unlike supplements, the minerals come in natural balance as nature created them.',
        wellnessMagnesiumFunctions: 'Often called the "relaxation mineral." Magnesium supports the nervous system, promotes deep sleep, and reduces stress and anxiety. It\'s also important for concentration and cognitive function. Many people don\'t get enough magnesium through their diet.',
        wellnessCalciumFunctions: 'Essential for strong bones and teeth, but also important for heart function and nerve signals. Calcium in water is absorbed as well or better than calcium from dairy products, according to several studies.',
        wellnessBicarbonateFunctions: 'Supports the body\'s natural acid-base balance and promotes good digestion. Bicarbonate can relieve heartburn and acid reflux, and supports the liver\'s detoxification processes.',
        wellnessPotassiumFunctions: 'Important for heart rhythm, blood pressure regulation, and cell balance. Potassium works together with sodium to maintain fluid balance and support nerve function.',
        wellnessRecommendation: 'For daily wellness, we recommend ROI for those who want maximum mineral intake, especially for sleep issues or stress. Marzia is an excellent choice for everyday use with a good balance between calcium and magnesium, and low sodium content. Gerolsteiner is well-suited for those who prefer sparkling water with moderate mineral content.'
    }
};

export default MineralGuidePage;
